import { useEffect, useState, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import {
  Lock, Unlock, RotateCcw, Undo2, History, X, RefreshCw, Smartphone, Trophy, ChevronUp, ShieldAlert, KeyRound,
  AlertTriangle, Shield, Check, CornerUpLeft,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const UNLOCK_SECONDS = 5;
const COOLDOWN_MS = 800;

const RED = '#E63946';
const BLUE = '#2563EB';
const GOLD = '#FFC24B';
const GREEN = '#2EE07C';
const AMBER = '#F59E0B';

// Long-name handling (owner ask 2026-07-09): never truncate a name to "Play…".
// Font drops through 3 tiers as the longest name grows; lines wrap up to 2 lines
// with overflow-wrap:anywhere so the worst case is a clean wrap, not a mid-word cut.
const nameScale = (str, base) => {
  const n = (str || '').length;
  const f = n > 22 ? 0.78 : n > 14 ? 0.88 : 1;
  return Math.round(base * f * 10) / 10;
};
const clampWrap = {
  margin: 0, overflowWrap: 'anywhere', display: '-webkit-box',
  WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
};

// Scoreboard team names: open play stacks each player on its own line (dimmed &
// connector); tournament has one team-name string that wraps. White per the
// neutral-name law — the colored rail + tint carry side identity.
function PanelNames({ players, name, base }) {
  const lines = players && players.length ? players.map(p => p.name) : [name || ''];
  const longest = lines.reduce((m, l) => Math.max(m, (l || '').length), 0);
  const fs = nameScale('x'.repeat(longest), base);
  return (
    <div style={{ width: '100%', minWidth: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 1 }}>
      {lines.map((l, i) => (
        <p key={i} style={{ ...clampWrap, fontSize: fs, fontWeight: 700, color: '#fff', textTransform: 'uppercase', lineHeight: 1.15 }}>
          {i > 0 && lines.length > 1 ? <><span style={{ color: '#8A8A82' }}>&amp; </span>{l}</> : l}
        </p>
      ))}
    </div>
  );
}

// Persistent device ID per browser — survives refresh, tab close, phone sleep
function getDeviceId() {
  let id = localStorage.getItem('scorer_device_id');
  if (!id) {
    id = 'dev_' + Math.random().toString(36).slice(2) + Date.now().toString(36);
    localStorage.setItem('scorer_device_id', id);
  }
  return id;
}
const DEVICE_ID = getDeviceId();

function fmtTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  let h = d.getHours(); const m = String(d.getMinutes()).padStart(2, '0');
  const ap = h >= 12 ? 'PM' : 'AM'; h = h % 12 || 12;
  return `${h}:${m} ${ap}`;
}

// Shared two-column point progression (RED | BLUE) — used by BOTH history
// drawers (tournament + open play) so the systems can't drift apart visually.
function HistoryColumns({ cols, events, showGame, testidPrefix }) {
  return (
    <div className="grid grid-cols-2 gap-2" data-testid={`${testidPrefix}-columns`}>
      {cols.map(({ side, color, name }) => {
        const pts = events
          .filter(ev => ev.action === 'point' && ev.team === side)
          .sort((x, y) => new Date(x.timestamp) - new Date(y.timestamp));
        return (
          <div key={side} data-testid={`${testidPrefix}-col-${side}`}>
            <div className="text-center text-xs font-black uppercase py-1.5 rounded-t-lg truncate px-1 sticky top-0 z-10"
              style={{ background: color, color: '#fff' }}>{name}</div>
            <div className="space-y-1 mt-1">
              {pts.length === 0 && <p className="text-[10px] text-center py-2" style={{ color: '#525252' }}>— no points —</p>}
              {pts.map(ev => (
                <div key={ev.id} className="flex items-center justify-between text-xs py-1.5 px-2 rounded"
                  style={{ background: `${color}14`, border: `1px solid ${color}33` }}>
                  <span className="font-black tabular-nums text-sm" style={{ color: '#fff' }}>
                    {side === 'a' ? ev.score_a_after : ev.score_b_after}
                  </span>
                  {showGame && <span className="font-bold" style={{ color }}>G{ev.game_number}</span>}
                  <span className="text-[10px]" style={{ color: '#525252' }}>{fmtTime(ev.timestamp)}</span>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// Open Play scorer state → the shared match-like object. The winner comes from
// the server (slot.winner) — the page used to default every finish to Team B.
const opMatchFrom = (d) => ({
  status: d.status,
  on_court: d.on_court !== false,
  winner_side: d.winner || null,
  walkover: !!d.walkover,
  session_status: d.session_status,
  team_a_name: d.team_a_name,
  team_b_name: d.team_b_name,
  team_a_players: d.team_a_players,
  team_b_players: d.team_b_players,
  score_a: d.score_a,
  score_b: d.score_b,
  court_name: d.court_name,
  config: { best_of: d.best_of, scoring_target: d.scoring_target || null, win_by: d.win_by || 2, games_to_win: 1 },
  tournament_name: d.session_name,
  round: '', match_number: '',
  games_won_a: 0, games_won_b: 0,
  pending_confirm: d.pending_confirm, pending_winner: d.pending_winner,
  queue_position: d.queue_position ?? null,
});

export default function SportsScorerPage({ qrTokenOverride = null, onFinishedChange = null, courtMode = false }) {
  const params = useParams();
  const qrToken = qrTokenOverride || params.qrToken;
  const [match, setMatch] = useState(null);
  const [error, setError] = useState('');
  const [locked, setLocked] = useState(true);
  const [countdown, setCountdown] = useState(0);
  const [flash, setFlash] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState(false);
  const [dims, setDims] = useState({ w: window.innerWidth, h: window.innerHeight });
  const [mobileMode, setMobileMode] = useState(false);
  const cooldownRef = useRef(0);
  const lockTimerRef = useRef(null);
  const tickRef = useRef(null);

  // Device lock state
  const [deviceClaimed, setDeviceClaimed] = useState(false);
  const [deviceLocked, setDeviceLocked] = useState(false); // another device holds the lock
  const [lockInfo, setLockInfo] = useState(null); // {match_info, requires_pin}
  const [pinInput, setPinInput] = useState('');
  const [pinError, setPinError] = useState('');
  const [overriding, setOverriding] = useState(false);
  const [transferred, setTransferred] = useState(false); // we got kicked by another device

  // Open Play / Match type state
  const [matchType, setMatchType] = useState('tournament'); // 'tournament' or 'open_play'
  const [openPlayData, setOpenPlayData] = useState(null);
  const [showForfeit, setShowForfeit] = useState(false);
  const [showMatchDecision, setShowMatchDecision] = useState(false);
  const [forfeitBusy, setForfeitBusy] = useState(false);
  const [opSwapped, setOpSwapped] = useState(false); // Open Play display-only side swap
  const [showOpActions, setShowOpActions] = useState(false); // Open Play match actions modal
  const [gateBusy, setGateBusy] = useState(false); // confirm/dispute in flight
  const [disputed, setDisputed] = useState(false); // amber "fix the score" banner after a dispute
  const [opDialog, setOpDialog] = useState(null); // Open Play in-page confirm { title, body, confirmLabel, onConfirm, color }
  const [woPin, setWoPin] = useState('');  // walkover = automatic win → session PIN (owner 2026-09-30)
  const [notice, setNotice] = useState(''); // Open Play non-error outcome line (e.g. absent → replaced by …)

  const load = useCallback(async () => {
    try {
      // Try tournament first
      const r = await axios.get(`${API}/sports/tournaments/score/${qrToken}`);
      setMatch(r.data);
      setMatchType('tournament');
    } catch (e) {
      if (e.response?.status === 404) {
        // Try open play
        try {
          const r2 = await axios.get(`${API}/sports/open-play/score/${qrToken}`);
          setOpenPlayData(r2.data);
          setMatchType('open_play');
          // Create a match-like object for shared UI
          setMatch(opMatchFrom(r2.data));
        } catch {
          setError('Match not found');
        }
      } else {
        setError(e.response?.data?.detail || 'Match not found');
      }
    }
  }, [qrToken]);

  const refreshOp = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/sports/open-play/score/${qrToken}`);
      setOpenPlayData(r.data);
      setMatch(opMatchFrom(r.data));
      return r.data;
    } catch { return null; }
  }, [qrToken]);

  // Claim device lock on mount
  const claimDevice = useCallback(async () => {
    try {
      const r = await axios.post(`${API}/sports/tournaments/score/${qrToken}/claim`, { device_id: DEVICE_ID });
      if (r.data.claimed) {
        setDeviceClaimed(true);
        setDeviceLocked(false);
        // Track which match this device is scoring (for multi-tab detection)
        localStorage.setItem('active_scorer_match', qrToken);
      } else {
        setDeviceLocked(true);
        setLockInfo(r.data);
      }
    } catch {
      // No claim endpoint or error — proceed without lock (backwards compatible)
      setDeviceClaimed(true);
    }
  }, [qrToken]);

  useEffect(() => {
    load().then(() => {
      // Only claim device for tournament matches (open play doesn't have device lock)
      // We check by trying to claim — if it fails for open play, that's fine
    });
  }, [load]);

  // Separate effect for device claim after matchType is known
  useEffect(() => {
    if (matchType === 'tournament' && match) claimDevice();
  }, [matchType, match, claimDevice]);

  // Open Play: check once whether another device already holds the scorer lock
  // (it used to re-check on every state change). A finished or cancelled match
  // shows its result, never the PIN wall.
  useEffect(() => {
    if (matchType !== 'open_play') return;
    axios.get(`${API}/sports/open-play/score/${qrToken}/lock-status?device_id=${DEVICE_ID}`)
      .then(r => {
        if (!['active', 'queued'].includes(r.data.slot_status)) { setDeviceClaimed(true); return; }
        if (r.data.locked_by && r.data.locked_by !== DEVICE_ID) {
          setDeviceLocked(true);
          setLockInfo({ requires_pin: true, message: 'Another device is already scoring this match.' });
        } else {
          setDeviceClaimed(true);
        }
      })
      .catch(() => setDeviceClaimed(true)); // fail open if lock-status unreachable
  }, [matchType, qrToken]);

  // Open Play: scoring opens only on a real court — a match still in line, or
  // called but waiting for a free court (TBA), shows 'Waiting for a court'.
  const opWaiting = matchType === 'open_play'
    && (match?.status === 'queued' || (match?.status === 'active' && match?.on_court === false));
  const opFinished = matchType === 'open_play' && !!match
    && (['completed', 'cancelled', 'bye'].includes(match.status) || match.session_status === 'ended');
  // Follow the owner's changes (sub's name, court assigned, result entered on the
  // console, match cancelled, session ended): re-read every 5 s while the match
  // is live and this device isn't mid-action.
  const actionBusy = busy || gateBusy;
  useEffect(() => {
    if (matchType !== 'open_play' || opFinished || actionBusy) return;
    const iv = setInterval(refreshOp, 5000);
    return () => clearInterval(iv);
  }, [matchType, opFinished, actionBusy, refreshOp]);
  useEffect(() => { if (onFinishedChange) onFinishedChange(opFinished); }, [opFinished, onFinishedChange]);

  // A refused Open Play action shows inline under the scoreboard; let it fade.
  const hasMatch = !!match;
  useEffect(() => {
    if (!error || !hasMatch || matchType !== 'open_play') return;
    const t = setTimeout(() => setError(''), 6000);
    return () => clearTimeout(t);
  }, [error, hasMatch, matchType]);
  useEffect(() => {
    if (!notice) return;
    const t = setTimeout(() => setNotice(''), 8000);
    return () => clearTimeout(t);
  }, [notice]);

  // Multi-tab detection: if another tab opens a different match, show banner
  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'active_scorer_match' && e.newValue && e.newValue !== qrToken) {
        setTransferred(true);
      }
    };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, [qrToken]);

  // Poll lock status every 15s to detect if we've been overridden (tournament only)
  useEffect(() => {
    if (!deviceClaimed) return;
    if (matchType !== 'tournament') return; // Open play uses its own lock-status endpoint
    const iv = setInterval(async () => {
      try {
        const r = await axios.get(`${API}/sports/tournaments/score/${qrToken}/lock-status?device_id=${DEVICE_ID}`);
        if (r.data.locked_by && r.data.locked_by !== DEVICE_ID && !r.data.is_owner) {
          setTransferred(true);
        }
      } catch { /* ignore */ }
    }, 15000);
    return () => clearInterval(iv);
  }, [deviceClaimed, qrToken, matchType]);

  const handleOverride = async () => {
    if (!pinInput.trim()) { setPinError('Enter the scorer PIN'); return; }
    setOverriding(true); setPinError('');
    try {
      const path = matchType === 'open_play'
        ? `/sports/open-play/score/${qrToken}/override`
        : `/sports/tournaments/score/${qrToken}/override`;
      const r = await axios.post(`${API}${path}`, { device_id: DEVICE_ID, pin: pinInput.trim() });
      if (r.data.claimed) {
        setDeviceClaimed(true);
        setDeviceLocked(false);
        setLockInfo(null);
        setPinInput('');
        localStorage.setItem('active_scorer_match', qrToken);
      }
    } catch (e) {
      const d = e.response?.data?.detail;
      setPinError(d?.message || d || 'Override failed');
    } finally { setOverriding(false); }
  };

  useEffect(() => {
    const onResize = () => setDims({ w: window.innerWidth, h: window.innerHeight });
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);
    return () => { window.removeEventListener('resize', onResize); window.removeEventListener('orientationchange', onResize); };
  }, []);

  const clearTimers = () => {
    if (lockTimerRef.current) clearTimeout(lockTimerRef.current);
    if (tickRef.current) clearInterval(tickRef.current);
  };

  const relock = useCallback(() => {
    clearTimers();
    setLocked(true);
    setCountdown(0);
  }, []);

  const unlock = () => {
    if (Date.now() < cooldownRef.current) return;
    if (match?.status === 'completed' || match?.status === 'bye') return;
    clearTimers();
    setLocked(false);
    setCountdown(UNLOCK_SECONDS);
    tickRef.current = setInterval(() => {
      setCountdown(c => {
        if (c <= 1) { relock(); return 0; }
        return c - 1;
      });
    }, 1000);
  };

  useEffect(() => () => clearTimers(), []);

  const score = async (side) => {
    if (transferred) return; // view-only mode
    if (Date.now() < cooldownRef.current) return;
    if (busy) return;
    cooldownRef.current = Date.now() + COOLDOWN_MS;
    relock(); // immediately lock per soft-lock UX
    setBusy(true);
    const label = side === 'a' ? (match.court_side_swapped ? 'BLUE' : 'RED') : (match.court_side_swapped ? 'RED' : 'BLUE');
    try {
      const r = await axios.post(`${API}/sports/tournaments/score/${qrToken}/point`, { team: side, device_id: DEVICE_ID });
      setMatch(r.data);
      setDisputed(false);
      // Match result → the gate (r.data.pending_confirm) renders; no flash.
      if (!r.data.pending_confirm) {
        setFlash({ side, label, game: r.data.game_just_ended });
        setTimeout(() => setFlash(null), 1300);
      }
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed');
      load();
    } finally { setBusy(false); }
  };

  const action = async (path, body) => {
    setBusy(true); relock();
    try {
      const r = await axios.post(`${API}/sports/tournaments/score/${qrToken}/${path}`, body || {});
      setMatch(r.data);
      if (path === 'undo') setDisputed(false);
    } catch (e) { setError(e.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };

  const openHistory = async () => {
    try { const r = await axios.get(`${API}/sports/tournaments/score/${qrToken}/history`); setHistory(r.data || []); setShowHistory(true); }
    catch { setShowHistory(true); }
  };

  // ─── Open Play lock UX ────────────────────────────────────────────────
  // "Tap to Unlock" → enables +1 scoring (auto-relocks after 5s or one tap).
  // Undo/End/Wins/Absent live behind the "Match Actions" modal in the fixed
  // bottom menu bar — deliberate two-step access with ZERO layout shift.

  // Open Play history
  const [opHistory, setOpHistory] = useState([]);
  const [showOpHistory, setShowOpHistory] = useState(false);
  const openOpHistory = async () => {
    try {
      const r = await axios.get(`${API}/sports/open-play/score/${qrToken}/history`);
      setOpHistory(r.data || []);
    } catch { setOpHistory([]); }
    setShowOpHistory(true);
  };

  // One error path for every Open Play action — a refusal is always shown (the
  // page used to show errors only before the match loaded).
  const opFail = (e, fallback) => {
    const st = e.response?.status;
    const d = e.response?.data?.detail;
    if (st === 403 && (d?.code === 'scorer_locked' || d?.code === 'pin_required')) {
      setDeviceLocked(true);
      setLockInfo({ locked_by: 'another device', message: d.message, requires_pin: true });
      return;
    }
    if (st === 409 && d?.code === 'not_called') {
      setMatch(prev => ({ ...prev, status: 'queued', queue_position: d.position ?? null }));
      return;
    }
    if (st === 409 && d?.code === 'not_on_court') {
      setMatch(prev => ({ ...prev, on_court: false }));
      return;
    }
    setError(d?.message || (typeof d === 'string' ? d : null) || fallback);
    if (st === 409 || st === 400) refreshOp(); // show the match as it really is now
  };

  // ─── Open Play: tap +1 for team a/b ───
  const openPlayScore = async (side) => {
    if (transferred) return;
    if (Date.now() < cooldownRef.current) return;
    if (busy) return;
    // Immediately relock after tap — one score per unlock (double-tap protection)
    relock();
    cooldownRef.current = Date.now() + COOLDOWN_MS;
    setBusy(true);
    const label = side === 'a' ? 'A' : 'B';
    try {
      const r = await axios.post(`${API}/sports/open-play/score/${qrToken}/point`,
        { team: side, device_id: DEVICE_ID });
      setDisputed(false);
      setMatch(prev => ({ ...prev, score_a: r.data.score_a, score_b: r.data.score_b,
        status: r.data.status, pending_confirm: r.data.pending_confirm, pending_winner: r.data.pending_winner }));
      // Match result → the gate renders (pending_confirm); no flash.
      if (!r.data.pending_confirm) {
        setFlash({ side, label });
        setTimeout(() => setFlash(null), 900);
      }
    } catch (e) {
      opFail(e, 'That point was not recorded — refresh.');
    } finally { setBusy(false); }
  };

  // ─── Match-result gate: Confirm (finalize) / Dispute (back to scoring) ───
  const confirmResult = async () => {
    if (gateBusy) return;
    setGateBusy(true); relock();
    const base = matchType === 'open_play' ? 'open-play' : 'tournaments';
    try {
      await axios.post(`${API}/sports/${base}/score/${qrToken}/confirm`, { device_id: DEVICE_ID });
      // Reload the finalized state (winner screen for both systems).
      if (matchType === 'open_play') {
        await refreshOp();
      } else {
        const r = await axios.get(`${API}/sports/tournaments/score/${qrToken}`);
        setMatch(r.data);
      }
      setFlash({ matchEnd: true });
      setTimeout(() => setFlash(null), 2600);
    } catch (e) {
      if (matchType === 'open_play') opFail(e, 'Confirm failed — refresh.');
      else { const d = e.response?.data?.detail; setError(d?.message || d || 'Confirm failed'); load(); }
    } finally { setGateBusy(false); }
  };

  const disputeResult = async () => {
    if (gateBusy) return;
    setGateBusy(true);
    const base = matchType === 'open_play' ? 'open-play' : 'tournaments';
    try {
      const r = await axios.post(`${API}/sports/${base}/score/${qrToken}/dispute`, { device_id: DEVICE_ID });
      if (matchType === 'open_play') {
        setMatch(prev => ({ ...prev, score_a: r.data.score_a, score_b: r.data.score_b,
          status: r.data.status, pending_confirm: false, pending_winner: null }));
      } else {
        setMatch(r.data);
      }
      setDisputed(true); // amber "fix the score" banner until the next point
    } catch (e) {
      if (matchType === 'open_play') opFail(e, 'Dispute failed — refresh.');
      else { const d = e.response?.data?.detail; setError(d?.message || d || 'Dispute failed'); load(); }
    } finally { setGateBusy(false); }
  };

  const openPlayUndo = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/sports/open-play/score/${qrToken}/undo`,
        { device_id: DEVICE_ID });
      setDisputed(false);
      setMatch(prev => ({ ...prev, score_a: r.data.score_a, score_b: r.data.score_b,
        pending_confirm: false, pending_winner: null }));
    } catch (e) {
      opFail(e, 'Undo failed — refresh.');
    } finally { setBusy(false); }
  };

  // A win nobody played for is a walkover: say what it does, ask for the PIN
  // (owner 2026-09-30). `lead` = why we're here (score not ahead / no points).
  const askWalkover = (winner, lead) => {
    const name = winner === 'a' ? (openPlayData?.team_a_name || 'Team A') : (openPlayData?.team_b_name || 'Team B');
    setWoPin('');
    setOpDialog({
      title: `Walkover for ${name}?`,
      body: `${lead} Keep scoring — or, only if a team can't play, record a walkover: ${name} get an automatic win and the game ends now. It goes on both teams' records as a walkover (W/O); no points are recorded. Out of time? Don't use walkover — the organizer ends the session and can save the score.`,
      confirmLabel: 'Record walkover', color: AMBER, cancelLabel: 'Keep scoring',
      walkover: true,
      onConfirm: (pin) => submitOpResult({ winner, walkover: true, ...(pin ? { pin } : {}) }),
    });
  };

  // Submit a result; a winner the score doesn't support gets a choice instead
  // of a silent save: keep scoring (fix the score) or record a walkover.
  const submitOpResult = async (body) => {
    setOpDialog(null);
    setBusy(true);
    try {
      await axios.put(`${API}/sports/open-play/score/${qrToken}/complete`, { ...body, device_id: DEVICE_ID });
      await refreshOp();
      setFlash({ matchEnd: true });
      setTimeout(() => setFlash(null), 2600);
    } catch (e) {
      const d = e.response?.data?.detail;
      if (e.response?.status === 409 && (d?.code === 'winner_trails' || d?.code === 'tied') && body.winner) {
        askWalkover(body.winner, `The score is ${d.score_a}–${d.score_b}, so that team isn't ahead.`);
      } else if (e.response?.status === 409 && d?.code === 'no_points' && body.winner) {
        askWalkover(body.winner, 'No points were scored.');
      } else {
        opFail(e, 'The result was not saved — refresh.');
      }
    } finally { setBusy(false); }
  };

  const openPlayEndMatch = () => {
    const sa = Number(match?.score_a || 0); const sb = Number(match?.score_b || 0);
    if (sa === 0 && sb === 0) {
      setError('No points scored yet — tap A or B to score first, or use A/B WINS buttons.');
      return;
    }
    if (sa === sb) {
      setError('Scores are tied — score another point to break the tie.');
      return;
    }
    const winnerName = sa > sb
      ? (openPlayData?.team_a_name || 'Team A')
      : (openPlayData?.team_b_name || 'Team B');
    setOpDialog({
      title: 'End match?',
      body: `${winnerName} win ${Math.max(sa, sb)}–${Math.min(sa, sb)}. This is final — the organizer can correct a wrong winner later.`,
      confirmLabel: 'End match', color: GREEN,
      onConfirm: () => submitOpResult({}),  // no winner → by current score
    });
  };

  // ─── Open Play: complete match (simple winner) ───
  const openPlayComplete = (winner) => {
    const name = winner === 'a' ? openPlayData?.team_a_name || 'Team A' : openPlayData?.team_b_name || 'Team B';
    const sa = Number(match?.score_a || 0); const sb = Number(match?.score_b || 0);
    if (!sa && !sb) {  // nothing on the board: a win without playing is a walkover
      askWalkover(winner, 'No points were scored.');
      return;
    }
    setOpDialog({
      title: `Winner: ${name}?`,
      body: `Court score: ${sa}–${sb}. This ends the match and updates the leaderboard.`,
      confirmLabel: 'Confirm winner', color: winner === 'a' ? RED : BLUE,
      onConfirm: () => submitOpResult({ winner }),
    });
  };

  // ─── Tournament: forfeit ───
  const handleForfeit = async (side) => {
    const teamName = side === 'a' ? match.team_a_name : match.team_b_name;
    if (!window.confirm(`Forfeit ${teamName}? The other team will win by walkover. This cannot be undone.`)) return;
    setForfeitBusy(true);
    try {
      const r = await axios.post(`${API}/sports/tournaments/score/${qrToken}/forfeit`, { forfeiting_team: side, device_id: DEVICE_ID });
      setMatch(r.data);
      setShowForfeit(false);
      setShowMatchDecision(false);
    } catch (e) {
      alert(e.response?.data?.detail || 'Forfeit failed');
    } finally { setForfeitBusy(false); }
  };

  if (error && !match) return (
    <div className="min-h-screen flex flex-col items-center justify-center p-6" style={{ background: '#0A0A0A', color: '#fff' }}>
      <X className="h-10 w-10 mb-3" style={{ color: RED }} />
      <p className="text-lg font-bold">{error}</p>
      <button onClick={load} className="mt-4 px-4 py-2 rounded-lg text-sm font-bold" style={{ background: RED, color: '#fff' }}>Retry</button>
    </div>
  );
  if (!match) return <div className="min-h-screen flex items-center justify-center" style={{ background: '#0A0A0A', color: '#737373' }}>Loading match…</div>;

  // Device lock — another device is scoring this match
  if (deviceLocked && lockInfo) {
    const mi = lockInfo.match_info || (matchType === 'open_play' && openPlayData
      ? { team_a: openPlayData.team_a_name, team_b: openPlayData.team_b_name, court: openPlayData.court_name } : {});
    return (
      <div data-testid="scorer-device-locked" className="min-h-screen flex flex-col items-center justify-center p-6" style={{ background: '#0A0A0A', color: '#fff' }}>
        <ShieldAlert className="h-12 w-12 mb-4" style={{ color: '#FFB300' }} />
        <h2 className="text-xl font-bold mb-2" style={{ fontFamily: 'Oswald, sans-serif' }}>SCORER ASSIGNED</h2>
        <p className="text-sm text-center mb-1" style={{ color: '#A3A3A3' }}>
          {mi.team_a && mi.team_b ? `${mi.team_a} vs ${mi.team_b}` : `Match ${mi.match_number || ''}`}
          {mi.court ? ` · ${mi.court}` : ''}
        </p>
        <p className="text-xs text-center mb-6" style={{ color: '#555' }}>Another device is already scoring this match.</p>
        {lockInfo.requires_pin ? (
          <div className="w-full max-w-xs space-y-3">
            <p className="text-xs text-center" style={{ color: '#737373' }}>Enter the Scorer PIN to take over:</p>
            <input data-testid="scorer-pin-input" type="password" inputMode="numeric" maxLength={6} value={pinInput}
              onChange={e => setPinInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleOverride()}
              placeholder="Scorer PIN"
              className="w-full text-center text-2xl tracking-widest"
              style={{ background: '#141414', border: '1px solid #2A2A2A', color: '#fff', borderRadius: 12, padding: '14px', outline: 'none', fontFamily: 'Oswald, sans-serif' }} />
            {pinError && <p className="text-xs text-center" style={{ color: RED }}>{pinError}</p>}
            <button data-testid="scorer-override-btn" onClick={handleOverride} disabled={overriding}
              className="w-full py-3 rounded-xl font-bold uppercase text-sm"
              style={{ background: '#FFB300', color: '#0A0A0A', opacity: overriding ? 0.6 : 1 }}>
              <KeyRound className="inline h-4 w-4 mr-1" /> {overriding ? 'Overriding…' : 'Override & Take Over'}
            </button>
          </div>
        ) : (
          <p className="text-xs text-center" style={{ color: '#555' }}>No scorer PIN is set for this session. Ask the organizer to set one in session settings.</p>
        )}
      </div>
    );
  }

  // Transferred — another device took over
  const showTransferBanner = transferred && !deviceLocked;

  const isLandscape = dims.w > dims.h;
  const isSmall = Math.min(dims.w, dims.h) < 500;
  const finished = match.status === 'completed' || match.status === 'bye' || match.status === 'cancelled'
    || (matchType === 'open_play' && match.session_status === 'ended');
  const winnerName = match.winner_side === 'a' ? match.team_a_name
    : match.winner_side === 'b' ? match.team_b_name : null;
  const errText = error ? (typeof error === 'string' ? error : (error?.message || 'That action was refused — refresh.')) : '';
  // Undo / End Match / Wins need a match on a court (the backend refuses them
  // while it waits in line) — faded with the reason.
  const scoreActionsBlockedReason = opWaiting ? 'Available once this match is called to a court' : null;
  const fadeIfBlocked = scoreActionsBlockedReason ? { opacity: 0.45, cursor: 'not-allowed' } : {};

  // Phone in portrait → ask to rotate (or use mobile mode)
  if (!isLandscape && isSmall && !mobileMode && !finished) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-8 text-center" style={{ background: '#0A0A0A', color: '#fff' }}>
        <Smartphone className="h-12 w-12 mb-4 animate-pulse" style={{ color: RED }} />
        <h2 className="text-xl font-bold mb-2" style={{ fontFamily: 'Oswald, sans-serif' }}>ROTATE TO LANDSCAPE</h2>
        <p className="text-sm mb-6" style={{ color: '#A3A3A3' }}>The scoring screen works best in landscape. Turn your phone sideways.</p>
        <button onClick={() => setMobileMode(true)} data-testid="use-mobile-mode" className="px-5 py-2.5 rounded-lg text-sm font-bold uppercase" style={{ background: '#1A1A1A', border: `1px solid ${RED}`, color: RED }}>
          Continue in Mobile Mode
        </button>
      </div>
    );
  }

  // Display side mapping (court_side_swapped flips which team is left/right)
  // Open Play uses a local display-only swap (no server state)
  const swapped = matchType === 'open_play' ? opSwapped : match.court_side_swapped;
  const leftSide = swapped ? 'b' : 'a';
  const rightSide = swapped ? 'a' : 'b';
  const sideData = {
    a: { name: match.team_a_name, players: match.team_a_players, score: match.score_a, gw: match.games_won_a, color: RED, label: 'RED' },
    b: { name: match.team_b_name, players: match.team_b_players, score: match.score_b, gw: match.games_won_b, color: BLUE, label: 'BLUE' },
  };
  const L = sideData[leftSide]; const R = sideData[rightSide];
  const stackControls = !isLandscape; // tablet portrait or mobile mode → controls below

  const isMobilePortrait = mobileMode && !isLandscape;
  // The court is ALWAYS split Left | Right — every orientation, both systems
  // (owner rule; tournament's old portrait vertical stacking removed 2026-07-09).
  const BTN_H = 64; // one score/unlock button height everywhere — zero layout shift
  const scoringTarget = matchType === 'open_play' ? (openPlayData?.scoring_target || null) : match?.config?.scoring_target;

  // Result gate: a match at its win result waits for Confirm/Dispute (backend
  // pending_confirm). Winner side + its display data for the overlay.
  const gateOpen = !finished && !!match.pending_confirm;
  const winSide = match.pending_winner === 'b' ? 'b' : 'a';
  const gWin = sideData[winSide];
  const gWinScore = Math.max(match.score_a || 0, match.score_b || 0);
  const gLoseScore = Math.min(match.score_a || 0, match.score_b || 0);

  // ── GAME / MATCH POINT — mirrors the backends' deuce rule exactly
  // (open play iter517 auto-complete + tournament is_game_won): a side is "at
  // point" when its NEXT point reaches the target with margin ≥ win_by. With
  // win_by=1 at deuce BOTH sides can be at point → generic "next rally wins".
  const pointState = (() => {
    if (finished || !match) return null;
    const target = scoringTarget || null;
    if (!target) return null;
    const winBy = (matchType === 'open_play' ? openPlayData?.win_by : match.config?.win_by) || 2;
    const sa = Number(match.score_a || 0); const sb = Number(match.score_b || 0);
    const aAt = sa + 1 >= target && (sa + 1 - sb) >= winBy;
    const bAt = sb + 1 >= target && (sb + 1 - sa) >= winBy;
    if (!aAt && !bAt) return null;
    let kind = 'match';
    if (matchType === 'tournament') {
      const need = match.config?.games_to_win || (Number(match.config?.best_of || 1) >> 1) + 1;
      const clinchA = aAt && (match.games_won_a || 0) + 1 >= need;
      const clinchB = bAt && (match.games_won_b || 0) + 1 >= need;
      kind = (clinchA || clinchB) ? 'match' : 'game';
    }
    const name = aAt && bAt ? null : (aAt ? match.team_a_name : match.team_b_name);
    return { kind, name };
  })();

  const TeamPanel = ({ d }) => {
    const scoreFontSize = isLandscape
      ? '22vh'
      : isMobilePortrait
        ? '16vw'
        : (isSmall ? '22vw' : '18vh');
    return (
      <div className="flex-1 flex flex-col items-center justify-center min-w-0 overflow-hidden px-2"
        style={{ background: `${d.color}14`, borderTop: `4px solid ${d.color}` }}>
        <div style={{ width: '100%', maxWidth: '100%' }}>
          <PanelNames players={d.players} name={d.name} base={isMobilePortrait ? 11 : (isSmall ? 12 : 15)} />
        </div>
        <p className="font-black tabular-nums leading-none" style={{ color: '#fff', fontSize: scoreFontSize }}>{d.score}</p>
        {matchType === 'tournament' && (
          <p className="text-xs" style={{ color: '#A3A3A3' }}>Games: {d.gw} / {match.config.games_to_win}</p>
        )}
        {scoringTarget && (
          <p style={{ color: '#525252', fontSize: isMobilePortrait ? 10 : 12, marginTop: 2 }}>to {scoringTarget}</p>
        )}
      </div>
    );
  };

  return (
    <div className="fixed inset-0 flex flex-col select-none overflow-hidden" style={{ background: '#0A0A0A', touchAction: 'manipulation' }}>
      {/* Transfer banner */}
      {showTransferBanner && (
        <div data-testid="scorer-transferred-banner" style={{ background: '#FFB30020', borderBottom: '2px solid #FFB300', padding: '10px 16px', textAlign: 'center', zIndex: 50 }}>
          <p style={{ fontSize: 13, fontWeight: 700, color: '#FFB300' }}>
            <ShieldAlert className="inline h-4 w-4 mr-1" /> Scoring transferred to another device. This tab is view-only.
          </p>
          <button onClick={load} className="text-xs mt-1 underline" style={{ color: '#FFB300' }}>Refresh to view scores</button>
        </div>
      )}
      {/* Top bar */}
      <div className="flex items-center justify-between px-3 py-1.5 shrink-0" style={{ background: '#000', borderBottom: '1px solid #1A1A1A' }}>
        <span className="text-xs font-bold" style={{ color: '#737373' }}>
          {matchType === 'open_play' ? (
            <>{match.tournament_name} {match.court_name && <span style={{ color: RED }}>· {match.court_name}</span>}</>
          ) : (
            <>{match.tournament_name} · R{match.round} M{match.match_number}</>
          )}
        </span>
        <div className="flex items-center gap-2 text-xs" style={{ color: '#737373' }}>
          {matchType === 'open_play' ? (
            <span style={{ color: '#22C55E' }}>OPEN PLAY{scoringTarget ? ` · to ${scoringTarget}` : ''}{(openPlayData?.win_by || 0) > 1 ? ` · win by ${openPlayData.win_by}` : ''}</span>
          ) : (
            <>
              <span>Best of {match.config.best_of} · to {match.config.scoring_target || '∞'}{(match.config.win_by || 0) > 1 ? ` · win by ${match.config.win_by}` : ''}</span>
              {match.court_name && <span style={{ color: RED }}>{match.court_name}</span>}
            </>
          )}
          {matchType === 'tournament' && (
            <button onClick={openHistory} data-testid="scorer-history-btn" style={{ color: '#A3A3A3' }}><History className="h-4 w-4" /></button>
          )}
        </div>
      </div>

      {/* Scoreboard */}
      <div className={`flex-1 flex ${stackControls ? 'flex-col' : 'flex-row'} relative min-h-0`}>
        <div className="flex flex-1 min-h-0 flex-row">
          <TeamPanel d={L} />
          <div className="flex items-center justify-center px-2" style={{ background: '#000' }}>
            <span className="font-black text-2xl" style={{ color: '#525252' }}>—</span>
          </div>
          <TeamPanel d={R} />
        </div>
      </div>

      {/* ─── Match-result gate (2026-07-09; made unmissable 2026-07-10) —
           a won match waits here for Confirm or Dispute. Nothing is finalized on
           the backend and NO winner shows on the tenant/public pages until the
           scorer taps Confirm. Full-screen + opaque + pulsing so it can't be
           scored past or walked away from unnoticed. ─── */}
      {gateOpen && (
        <div data-testid="scorer-result-gate" className="absolute inset-0 z-40 flex flex-col items-center justify-center px-5"
          style={{ background: '#04240F' }}>
          {/* Pulsing call-to-action banner — the part that catches the eye */}
          <div className="w-full text-center animate-pulse" style={{ marginBottom: 'clamp(10px, 2.5vh, 24px)' }}>
            <p style={{ margin: 0, fontFamily: 'Oswald, sans-serif', fontWeight: 800, letterSpacing: 2, textTransform: 'uppercase', color: GREEN, fontSize: 'clamp(20px, 5.5vw, 36px)', lineHeight: 1.05 }}>
              ✋ Tap to confirm winner
            </p>
            <p style={{ margin: '7px auto 0', maxWidth: 420, fontSize: 'clamp(11px, 2.6vw, 15px)', color: '#8FBF9F', lineHeight: 1.35 }}>
              The match isn’t final until you confirm — no winner shows on the board until then.
            </p>
          </div>

          <Trophy style={{ color: GOLD, width: 'clamp(30px, 8vw, 56px)', height: 'clamp(30px, 8vw, 56px)', marginBottom: 'clamp(4px, 1.2vh, 12px)' }} />
          <div style={{ width: '100%', maxWidth: 520, textAlign: 'center' }}>
            <PanelNames players={gWin.players} name={gWin.name} base={20} />
          </div>
          <p style={{ margin: '6px 0 2px', fontFamily: 'Oswald, sans-serif', fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', color: '#9A9A92', fontSize: 'clamp(12px, 3vw, 18px)' }}>win</p>
          <p style={{ margin: '0 0 clamp(16px, 3vh, 30px)', fontFamily: 'Oswald, sans-serif', fontWeight: 900, color: '#fff', fontSize: 'clamp(30px, 9vw, 64px)', lineHeight: 1 }}>
            {gWinScore} <span style={{ color: '#525252' }}>&ndash;</span> {gLoseScore}
          </p>

          <button data-testid="scorer-confirm-result" onClick={confirmResult} disabled={gateBusy}
            style={{ width: '100%', maxWidth: 520, height: 'clamp(64px, 10vh, 84px)', border: 'none', borderRadius: 14, background: `linear-gradient(135deg, #1FB865, ${GREEN})`, color: '#04240F', fontFamily: 'Oswald, sans-serif', fontSize: 'clamp(18px, 5vw, 26px)', fontWeight: 800, letterSpacing: 1.5, textTransform: 'uppercase', boxShadow: '0 0 40px rgba(46,224,124,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, opacity: gateBusy ? 0.6 : 1, cursor: 'pointer' }}>
            <Check style={{ width: 'clamp(20px, 5vw, 28px)', height: 'clamp(20px, 5vw, 28px)' }} /> {gateBusy ? 'Saving…' : (matchType === 'tournament' ? 'Confirm & advance' : 'Confirm winner')}
          </button>
          <button data-testid="scorer-dispute-result" onClick={disputeResult} disabled={gateBusy}
            style={{ width: '100%', maxWidth: 520, height: 'clamp(44px, 6vh, 56px)', marginTop: 12, border: `1px solid ${AMBER}66`, borderRadius: 12, background: 'rgba(245,158,11,0.12)', color: AMBER, fontFamily: 'Oswald, sans-serif', fontSize: 'clamp(12px, 3.2vw, 16px)', fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, opacity: gateBusy ? 0.6 : 1, cursor: 'pointer' }}>
            <CornerUpLeft style={{ width: 'clamp(16px, 4vw, 22px)', height: 'clamp(16px, 4vw, 22px)' }} /> Dispute — fix score
          </button>
          {errText && (
            <p data-testid="scorer-gate-error" role="alert" onClick={() => setError('')}
              style={{ marginTop: 14, maxWidth: 520, textAlign: 'center', fontSize: 'clamp(12px, 3.2vw, 15px)', fontWeight: 700, color: '#FFB4B4', cursor: 'pointer' }}>
              {errText}
            </p>
          )}
        </div>
      )}

      {/* Flash confirmation */}
      {flash && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-30">
          {flash.matchEnd ? (
            <div className="text-center px-6 py-5 rounded-2xl" style={{ background: 'rgba(0,0,0,0.92)', border: `2px solid #22C55E` }}>
              <Trophy className="h-10 w-10 mx-auto mb-2" style={{ color: '#22C55E' }} />
              <p className="text-xl font-black" style={{ color: '#fff', fontFamily: 'Oswald, sans-serif' }}>MATCH COMPLETE</p>
              {winnerName && <p className="text-sm mt-1" style={{ color: '#22C55E' }}>Winner: {winnerName}</p>}
            </div>
          ) : (
            <div className="px-6 py-3 rounded-full text-2xl font-black animate-bounce" style={{ background: sideData[flash.side].color, color: '#fff' }}>
              ✓ +1 {flash.label}{flash.game ? ' · GAME!' : ''}
            </div>
          )}
        </div>
      )}

      {/* ─── Control band — ONE structure for BOTH systems (2026-07-09):
           banner slot (point state > lock status) → 72px main slot (unlock ⇄ +1,
           zero layout shift) → per-system menu row. Desktop caps at 720px. ─── */}
      <div className="shrink-0 p-3" style={{ background: '#000', borderTop: '1px solid #1A1A1A' }}>
        <div style={{ maxWidth: 720, margin: '0 auto' }}>
        {finished ? (
          <div className="text-center py-3">
            {match.status === 'cancelled' ? (
              <p data-testid="scorer-cancelled" className="text-lg font-black" style={{ color: '#737373', fontFamily: 'Oswald, sans-serif' }}>
                MATCH CANCELLED — no result recorded
              </p>
            ) : match.status !== 'completed' && match.status !== 'bye' ? (
              <p data-testid="scorer-session-ended" className="text-lg font-black" style={{ color: '#737373', fontFamily: 'Oswald, sans-serif' }}>
                SESSION ENDED — scoring is closed
              </p>
            ) : (
              <p data-testid="scorer-winner" className="text-lg font-black" style={{ color: '#22C55E', fontFamily: 'Oswald, sans-serif' }}>
                <Trophy className="inline h-5 w-5 mr-1" /> {match.status === 'bye' ? 'BYE — Advanced' : (winnerName ? `WINNER: ${winnerName}` : 'MATCH COMPLETE')}
              </p>
            )}
            {match.status === 'completed' && matchType === 'open_play' && (
              <p className="text-xs mt-1" style={{ color: '#A3A3A3' }}>
                Final {Math.max(match.score_a || 0, match.score_b || 0)}–{Math.min(match.score_a || 0, match.score_b || 0)}{match.walkover ? ' · walkover' : ''}
              </p>
            )}
            {courtMode && (
              <p data-testid="scorer-court-next" className="text-xs mt-2" style={{ color: '#737373' }}>
                The next match on this court opens here automatically.
              </p>
            )}
            {match.forfeit && <p className="text-xs mt-1" style={{ color: '#F59E0B' }}>Match ended by forfeit</p>}
          </div>
        ) : (
          <>
            {/* Banner slot — fixed height so no state swap ever shifts layout.
                Priority: disputed (fix-the-score) > game/match point > lock state. */}
            <div className="flex items-stretch justify-center mb-2" style={{ height: 28 }}>
              {opWaiting ? (
                <div data-testid="op-waiting-banner" className="flex items-center justify-center gap-2"
                  style={{ borderRadius: 999, padding: '0 12px', background: 'rgba(245,158,11,0.10)', border: `1px solid ${AMBER}80` }}>
                  <span className="animate-pulse" style={{ width: 5, height: 5, borderRadius: '50%', background: AMBER, flexShrink: 0 }} />
                  <span className="truncate" style={{ fontFamily: 'Oswald, sans-serif', fontSize: 11, fontWeight: 700, letterSpacing: 1.5, textTransform: 'uppercase', color: AMBER }}>
                    {match.status === 'active' ? 'Called — waiting for a free court'
                      : `Waiting for a court${match.queue_position ? ` — #${match.queue_position} in line` : ''}`}
                  </span>
                </div>
              ) : disputed ? (
                <div data-testid="scorer-dispute-banner" className="flex items-center justify-center gap-2"
                  style={{ borderRadius: 999, padding: '0 12px', background: 'rgba(245,158,11,0.10)', border: `1px solid ${AMBER}80` }}>
                  <AlertTriangle className="h-3.5 w-3.5" style={{ color: AMBER, flexShrink: 0 }} />
                  <span className="truncate" style={{ fontFamily: 'Oswald, sans-serif', fontSize: 11, fontWeight: 700, letterSpacing: 1.5, textTransform: 'uppercase', color: AMBER }}>Result disputed — fix the score</span>
                </div>
              ) : pointState ? (
                <div data-testid="scorer-point-banner" className="flex items-center justify-center gap-2"
                  style={{ minWidth: 0, borderRadius: 999, padding: '0 12px',
                    background: pointState.kind === 'match' ? 'rgba(255,194,75,0.12)' : 'rgba(255,194,75,0.08)',
                    border: `1px solid rgba(255,194,75,${pointState.kind === 'match' ? 0.5 : 0.3})`,
                    boxShadow: pointState.kind === 'match' ? '0 0 14px rgba(255,194,75,0.15)' : 'none' }}>
                  <span className={pointState.kind === 'match' ? 'animate-pulse' : ''}
                    style={{ width: 5, height: 5, borderRadius: '50%', background: GOLD, boxShadow: pointState.kind === 'match' ? '0 0 6px #FFC24B' : 'none', flexShrink: 0 }} />
                  <span className="truncate" style={{ fontFamily: 'Oswald, sans-serif', fontSize: 10.5, fontWeight: 700, letterSpacing: 1.5, textTransform: 'uppercase', color: GOLD, textShadow: pointState.kind === 'match' ? '0 0 10px rgba(255,194,75,0.6)' : 'none' }}>
                    {pointState.kind === 'match' ? 'Match point' : 'Game point'}{pointState.name ? ` — ${pointState.name}` : ' — next rally wins'}
                  </span>
                </div>
              ) : locked ? (
                <div className="flex items-center justify-center gap-2">
                  <Lock className="h-3.5 w-3.5" style={{ color: '#737373' }} />
                  <span className="text-xs font-bold" style={{ color: '#737373' }}>LOCKED — tap below to enable scoring</span>
                </div>
              ) : (
                <div className="flex items-center justify-center gap-2">
                  <Unlock className="h-4 w-4" style={{ color: '#22C55E' }} />
                  <span className="text-xs font-bold" style={{ color: '#22C55E' }}>
                    UNLOCKED{countdown > 0 ? ` · auto-locks in ${countdown}s` : ''} — {matchType === 'open_play' ? 'tap once to score' : 'tap who scored'}
                  </span>
                </div>
              )}
            </div>
            {/* Main slot — unlock button swaps in-place with the two +1 buttons (same footprint).
                Both systems now score by team NAME + "+1" (side color already signals which). */}
            {opWaiting ? (
              // Not called to a court yet: scoring is closed (the backend refuses the
              // point). Faded with the reason — never a live-looking +1 that fails.
              <button data-testid="op-waiting-score" disabled
                className="w-full flex items-center justify-center gap-2 rounded-xl font-bold uppercase tracking-wider"
                style={{ background: '#141414', border: '2px dashed #2A2A2A', color: '#737373', height: BTN_H, fontSize: isMobilePortrait ? 12 : 14, cursor: 'not-allowed', padding: '0 12px', textAlign: 'center' }}>
                <Lock className="h-4 w-4 shrink-0" /> {match.status === 'active' ? 'Scoring opens when a court frees up' : 'Scoring opens when this match is called to a court'}
              </button>
            ) : locked ? (
              <button data-testid="tap-to-unlock" onClick={unlock}
                className="w-full flex items-center justify-center gap-2 rounded-xl font-black uppercase tracking-wider transition-transform active:scale-95"
                style={{ background: '#1A1A1A', border: '2px solid #2A2A2A', color: '#fff', height: BTN_H, fontSize: isMobilePortrait ? 15 : (isLandscape ? 17 : 20) }}>
                <Lock className="h-5 w-5" /> Tap to Unlock
              </button>
            ) : (
              <div className="flex gap-3">
                {[{ side: leftSide, d: L }, { side: rightSide, d: R }].map(({ side, d }) => (
                  <button key={side}
                    data-testid={matchType === 'open_play' ? `op-score-${side}` : (side === leftSide ? 'score-left' : 'score-right')}
                    onClick={() => (matchType === 'open_play' ? openPlayScore(side) : score(side))} disabled={busy}
                    className="flex-1 rounded-xl transition-transform active:scale-95 flex flex-col items-center justify-center"
                    style={{ background: d.color, color: '#fff', height: BTN_H, padding: '0 6px', gap: 1 }}>
                    <span style={{ ...clampWrap, fontFamily: 'Oswald, sans-serif', fontSize: nameScale(d.name, isMobilePortrait ? 11 : 13), fontWeight: 600, textTransform: 'uppercase', lineHeight: 1.1, textAlign: 'center', width: '100%' }}>{d.name}</span>
                    <span style={{ fontFamily: 'Oswald, sans-serif', fontSize: 13, fontWeight: 700 }}>+1</span>
                  </button>
                ))}
              </div>
            )}
          </>
        )}

        {/* Tournament action bar */}
        {!finished && matchType === 'tournament' && (
          <div className="flex items-center justify-center gap-2 mt-1 flex-wrap">
            <button data-testid="scorer-undo" onClick={() => action('undo')} disabled={busy} className="flex items-center gap-1 text-xs px-2" style={{ color: '#A3A3A3', minHeight: 44 }}><Undo2 className="h-3.5 w-3.5" /> Undo</button>
            <button data-testid="scorer-switch" onClick={() => action('switch-sides')} disabled={busy} className="flex items-center gap-1 text-xs px-2" style={{ color: '#A3A3A3', minHeight: 44 }}><RotateCcw className="h-3.5 w-3.5" /> Switch Sides</button>
            <button data-testid="scorer-endgame" onClick={() => { if (window.confirm('End current game by current score?')) action('end-game'); }} disabled={busy} className="flex items-center gap-1 text-xs px-2" style={{ color: '#A3A3A3', minHeight: 44 }}><ChevronUp className="h-3.5 w-3.5" /> End Game</button>
            <button data-testid="scorer-match-decision" onClick={() => setShowMatchDecision(true)} className="flex items-center gap-1 text-xs px-2" style={{ color: '#F59E0B', minHeight: 44 }}>
              <Shield className="h-3.5 w-3.5" /> Match Decision
            </button>
            <button onClick={load} className="flex items-center gap-1 text-xs px-2" style={{ color: '#A3A3A3', minHeight: 44 }}><RefreshCw className="h-3.5 w-3.5" /> Refresh</button>
          </div>
        )}

        {/* A refused Open Play action (the full-screen error only covers a failed
            load) — shown inline, clears itself. */}
        {matchType === 'open_play' && errText && !gateOpen && (
          <p data-testid="op-inline-error" role="alert" onClick={() => setError('')}
            className="text-xs text-center mt-1 px-2" style={{ color: RED, cursor: 'pointer' }}>
            {errText}
          </p>
        )}
        {matchType === 'open_play' && notice && !errText && (
          <p data-testid="op-inline-notice" role="status" onClick={() => setNotice('')}
            className="text-xs text-center mt-1 px-2" style={{ color: AMBER, cursor: 'pointer' }}>
            {notice}
          </p>
        )}
        {/* Open Play persistent menu bar — always rendered, never shifts */}
        {!finished && matchType === 'open_play' && (
          <div className="flex items-center justify-center gap-2 mt-1 flex-wrap">
            <button data-testid="op-history" onClick={openOpHistory} className="flex items-center gap-1 text-xs px-2" style={{ color: '#A3A3A3', minHeight: 44 }}><History className="h-3.5 w-3.5" /> History</button>
            <button data-testid="op-swap-sides" onClick={() => setOpSwapped(s => !s)} className="flex items-center gap-1 text-xs px-2" style={{ color: '#A3A3A3', minHeight: 44 }}><RotateCcw className="h-3.5 w-3.5" /> Swap Sides</button>
            <button data-testid="op-match-actions" onClick={() => setShowOpActions(true)} className="flex items-center gap-1 text-xs px-2" style={{ color: '#F59E0B', minHeight: 44 }}>
              <Shield className="h-3.5 w-3.5" /> Match Actions
            </button>
            <button onClick={load} className="flex items-center gap-1 text-xs px-2" style={{ color: '#A3A3A3', minHeight: 44 }}><RefreshCw className="h-3.5 w-3.5" /> Refresh</button>
          </div>
        )}
        </div>
      </div>

      {/* ─── Match Actions Modal (Open Play — deliberate two-step access, no layout shift) ─── */}
      {showOpActions && matchType === 'open_play' && (
        <div className="absolute inset-0 z-50 flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.85)' }} onClick={() => setShowOpActions(false)}>
          <div className="w-full max-w-sm p-5 rounded-2xl mx-4" style={{ background: '#141414', border: '1px solid #2A2A2A' }} onClick={e => e.stopPropagation()} data-testid="op-actions-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-bold uppercase" style={{ fontFamily: 'Oswald, sans-serif', color: '#F59E0B' }}>
                <Shield className="inline h-4 w-4 mr-1" /> Match Actions
              </h3>
              <button data-testid="op-actions-close" onClick={() => setShowOpActions(false)} style={{ color: '#555' }}><X className="h-5 w-5" /></button>
            </div>
            {scoreActionsBlockedReason && (
              <p data-testid="op-actions-blocked-reason" className="text-xs mb-3" style={{ color: '#A3A3A3' }}>
                Undo, End Match and Wins: {scoreActionsBlockedReason.toLowerCase()}.
              </p>
            )}
            <div className="grid grid-cols-2 gap-2">
              <button data-testid="op-undo" onClick={() => { if (scoreActionsBlockedReason) return; setShowOpActions(false); openPlayUndo(); }} disabled={busy || !!scoreActionsBlockedReason}
                className="flex flex-col items-center justify-center gap-1 rounded-xl py-3 font-bold text-xs uppercase transition-transform active:scale-95"
                style={{ background: '#1E1E1E', border: '1px solid #2E2E2E', color: '#A3A3A3', minHeight: 56, ...fadeIfBlocked }}>
                <Undo2 className="h-4 w-4" /> Undo Last Point
              </button>
              <button data-testid="op-end-match" onClick={() => { if (scoreActionsBlockedReason) return; setShowOpActions(false); openPlayEndMatch(); }} disabled={busy || !!scoreActionsBlockedReason}
                className="flex flex-col items-center justify-center gap-1 rounded-xl py-3 font-bold text-xs uppercase transition-transform active:scale-95"
                style={{ background: '#22C55E15', border: '1px solid #22C55E44', color: '#22C55E', minHeight: 56, ...fadeIfBlocked }}>
                <Trophy className="h-4 w-4" /> End Match
              </button>
              <button data-testid="op-win-a" onClick={() => { if (scoreActionsBlockedReason) return; setShowOpActions(false); openPlayComplete('a'); }} disabled={busy || !!scoreActionsBlockedReason}
                className="flex flex-col items-center justify-center gap-1 rounded-xl py-3 font-bold text-xs uppercase transition-transform active:scale-95 px-1"
                style={{ background: `${RED}15`, border: `1px solid ${RED}44`, color: RED, minHeight: 56, ...fadeIfBlocked }}>
                <Trophy className="h-4 w-4" />
                <span className="truncate max-w-full">{openPlayData?.team_a_name ? openPlayData.team_a_name.split(' ')[0] : 'A'} Wins</span>
              </button>
              <button data-testid="op-win-b" onClick={() => { if (scoreActionsBlockedReason) return; setShowOpActions(false); openPlayComplete('b'); }} disabled={busy || !!scoreActionsBlockedReason}
                className="flex flex-col items-center justify-center gap-1 rounded-xl py-3 font-bold text-xs uppercase transition-transform active:scale-95 px-1"
                style={{ background: `${BLUE}15`, border: `1px solid ${BLUE}44`, color: BLUE, minHeight: 56, ...fadeIfBlocked }}>
                <Trophy className="h-4 w-4" />
                <span className="truncate max-w-full">{openPlayData?.team_b_name ? openPlayData.team_b_name.split(' ')[0] : 'B'} Wins</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─── Open Play in-page confirm (End Match / Wins / walkover) ─── */}
      {opDialog && (
        <div className="absolute inset-0 z-[60] flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.88)' }}
          onClick={() => setOpDialog(null)}>
          <div data-testid="op-dialog" className="w-full max-w-xs p-5 rounded-2xl mx-4" style={{ background: '#141414', border: `1px solid ${opDialog.color}55` }}
            onClick={e => e.stopPropagation()}>
            <h3 className="text-sm font-black uppercase mb-2" style={{ fontFamily: 'Oswald, sans-serif', color: '#fff' }}>{opDialog.title}</h3>
            <p className="text-xs mb-5" style={{ color: '#A3A3A3', lineHeight: 1.5 }}>{opDialog.body}</p>
            {opDialog.walkover && openPlayData?.walkover_needs_pin && (
              <label className="flex items-center gap-2 -mt-2 mb-4 text-xs">
                <span className="font-bold" style={{ color: '#FCD34D' }}>Session PIN</span>
                <input data-testid="op-dialog-pin" type="password" inputMode="numeric" autoComplete="off"
                  maxLength={6} value={woPin} placeholder="••••"
                  onChange={e => setWoPin(e.target.value.replace(/\D/g, ''))}
                  style={{ background: '#0A0A0A', border: '1px solid #2A2A2A', color: '#fff', borderRadius: 8,
                           padding: '6px 8px', width: 96, letterSpacing: 4, fontWeight: 800, outline: 'none' }} />
              </label>
            )}
            <div className="flex gap-2">
              <button data-testid="op-dialog-cancel" onClick={() => setOpDialog(null)}
                className="flex-1 py-2.5 rounded-lg text-sm font-bold"
                style={{ background: '#1A1A1A', color: '#A3A3A3', border: '1px solid #2A2A2A' }}>
                {opDialog.cancelLabel || 'Cancel'}
              </button>
              {(() => {
                const blocked = opDialog.walkover && openPlayData?.walkover_needs_pin && !/^\d{4,6}$/.test(woPin);
                return (
              <button data-testid="op-dialog-confirm" onClick={() => opDialog.onConfirm(woPin)} disabled={busy || blocked}
                className="flex-1 py-2.5 rounded-lg text-sm font-black uppercase"
                style={{ background: opDialog.color, color: '#0A0A0A', opacity: busy || blocked ? 0.5 : 1, fontFamily: 'Oswald, sans-serif' }}>
                {busy ? 'Saving…' : opDialog.confirmLabel}
              </button>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* ─── Match Decision Modal (Tournament — double-unlock forfeit) ─── */}
      {showMatchDecision && matchType === 'tournament' && (
        <div className="absolute inset-0 z-50 flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.85)' }} onClick={() => { setShowMatchDecision(false); setShowForfeit(false); }}>
          <div className="w-full max-w-sm p-5 rounded-2xl mx-4" style={{ background: '#141414', border: '1px solid #2A2A2A' }} onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-bold uppercase" style={{ fontFamily: 'Oswald, sans-serif', color: '#F59E0B' }}>
                <Shield className="inline h-4 w-4 mr-1" /> Match Decision
              </h3>
              <button onClick={() => { setShowMatchDecision(false); setShowForfeit(false); }} style={{ color: '#555' }}><X className="h-5 w-5" /></button>
            </div>
            {!showForfeit ? (
              <div className="space-y-2">
                <button data-testid="match-decision-forfeit" onClick={() => setShowForfeit(true)}
                  className="w-full px-4 py-3 rounded-xl text-sm font-bold uppercase text-left"
                  style={{ background: '#EF444415', border: '1px solid #EF444444', color: '#EF4444' }}>
                  <AlertTriangle className="inline h-4 w-4 mr-2" />Team Forfeited
                  <p className="text-xs font-normal mt-1 normal-case" style={{ color: '#737373' }}>
                    A team didn't show up or withdrew. The other team wins by walkover.
                  </p>
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                <p className="text-xs" style={{ color: '#F59E0B' }}>Which team forfeited?</p>
                <button data-testid="forfeit-team-a" onClick={() => handleForfeit('a')} disabled={forfeitBusy}
                  className="w-full px-4 py-3 rounded-xl text-sm font-bold uppercase"
                  style={{ background: `${RED}15`, border: `1px solid ${RED}44`, color: RED }}>
                  {match.team_a_name} Forfeited
                </button>
                <button data-testid="forfeit-team-b" onClick={() => handleForfeit('b')} disabled={forfeitBusy}
                  className="w-full px-4 py-3 rounded-xl text-sm font-bold uppercase"
                  style={{ background: `${BLUE}15`, border: `1px solid ${BLUE}44`, color: BLUE }}>
                  {match.team_b_name} Forfeited
                </button>
                <button onClick={() => setShowForfeit(false)} className="w-full text-xs py-2" style={{ color: '#555' }}>Cancel</button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* History drawer — two columns (RED progression | BLUE progression) */}
      {showHistory && (
        <div className="absolute inset-0 z-40 flex justify-end" style={{ background: 'rgba(0,0,0,0.7)' }} onClick={() => setShowHistory(false)}>
          <div className="w-full max-w-md h-full p-4 overflow-y-auto" style={{ background: '#0F0F0F', borderLeft: '1px solid #2A2A2A' }} onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm font-bold" style={{ color: '#fff' }}>Score History</p>
              <button data-testid="scorer-history-close" onClick={() => setShowHistory(false)} style={{ color: '#737373' }}><X className="h-5 w-5" /></button>
            </div>
            <button onClick={() => { action('undo'); setShowHistory(false); }} className="w-full mb-3 py-2 rounded-lg text-xs font-bold" style={{ background: '#1A1A1A', color: RED, border: '1px solid #2A2A2A' }}>Undo Last Point</button>
            {history.length === 0 ? (
              <p className="text-xs" style={{ color: '#525252' }}>No events yet.</p>
            ) : (
              <>
                <HistoryColumns testidPrefix="scorer-history" showGame events={history}
                  cols={[{ side: 'a', color: sideData.a.color, name: sideData.a.name },
                         { side: 'b', color: sideData.b.color, name: sideData.b.name }]} />
                {/* Game results + non-point actions (chronological) */}
                {history.some(ev => ev.action !== 'point') && (
                  <div className="mt-4 pt-3" style={{ borderTop: '1px solid #1A1A1A' }}>
                    <p className="text-[10px] font-bold uppercase mb-1.5" style={{ color: '#525252' }}>Match Events</p>
                    <div className="space-y-1">
                      {history.filter(ev => ev.action !== 'point').map(ev => (
                        <div key={ev.id} className="flex items-center justify-between text-[11px] py-1 px-2 rounded" style={{ background: '#0A0A0A' }}>
                          <span style={{ color: '#A3A3A3' }}>{ev.action.replace(/_/g, ' ').toUpperCase()} · G{ev.game_number}</span>
                          <span style={{ color: '#525252' }}>{ev.score_a_after}–{ev.score_b_after} · {fmtTime(ev.timestamp)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
      {/* ─── Open Play History Drawer ─── */}
      {showOpHistory && (
        <div className="absolute inset-0 z-40 flex justify-end" style={{ background: 'rgba(0,0,0,0.7)' }} onClick={() => setShowOpHistory(false)}>
          <div className="w-full max-w-md h-full p-4 overflow-y-auto" style={{ background: '#0F0F0F', borderLeft: '1px solid #2A2A2A' }} onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <div>
                <p className="text-sm font-bold" style={{ color: '#fff' }}>Score History</p>
                <p className="text-xs" style={{ color: '#555' }}>
                  {openPlayData?.team_a_name || 'Team A'} vs {openPlayData?.team_b_name || 'Team B'}
                </p>
              </div>
              <button data-testid="op-history-close" onClick={() => setShowOpHistory(false)} style={{ color: '#737373' }}><X className="h-5 w-5" /></button>
            </div>
            {/* Current score summary */}
            <div className="flex items-center justify-center gap-4 mb-4 py-3 rounded-xl" style={{ background: '#141414', border: '1px solid #222' }}>
              <span className="text-3xl font-black tabular-nums" style={{ color: RED, fontFamily: 'Oswald, sans-serif' }}>{match?.score_a || 0}</span>
              <span className="text-sm font-bold" style={{ color: '#444' }}>—</span>
              <span className="text-3xl font-black tabular-nums" style={{ color: BLUE, fontFamily: 'Oswald, sans-serif' }}>{match?.score_b || 0}</span>
            </div>
            {opHistory.length === 0 ? (
              <p className="text-xs text-center py-6" style={{ color: '#525252' }}>No scoring events yet.</p>
            ) : (
              <HistoryColumns testidPrefix="op-history" showGame={false} events={opHistory}
                cols={[
                  { side: 'a', color: RED, name: openPlayData?.team_a_name || 'Team A' },
                  { side: 'b', color: BLUE, name: openPlayData?.team_b_name || 'Team B' },
                ]} />
            )}
            {/* Undo events */}
            {opHistory.some(ev => ev.action === 'undo') && (
              <div className="mt-4 pt-3" style={{ borderTop: '1px solid #1A1A1A' }}>
                <p className="text-[10px] font-bold uppercase mb-1.5" style={{ color: '#525252' }}>Corrections</p>
                <div className="space-y-1">
                  {opHistory.filter(ev => ev.action === 'undo').map(ev => (
                    <div key={ev.id} className="flex items-center justify-between text-[11px] py-1 px-2 rounded" style={{ background: '#0A0A0A' }}>
                      <span style={{ color: '#F59E0B' }}>UNDO (Team {ev.team.toUpperCase()})</span>
                      <span style={{ color: '#525252' }}>{ev.score_a_after}–{ev.score_b_after} · {fmtTime(ev.timestamp)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
